import { useEffect } from 'react';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { setAndShowErrorToast } from '../../store/config';
import { LoadingState } from '../../utils/loadingState';
import { agenciesApi } from 'Api/Agencies/agenciesApi';
import { AppDispatch } from '../../store/store';
import { AgencyAgentModel } from 'Api/Agencies/Types/Agencies';
import { AgencyInfo, useGetAgencyInformationForUser } from '@urbanx/agx.agencies.client';

interface AgenciesState {
  agencyInfo: {
    loadingState: LoadingState;
    info: AgencyInfo;
  };
  agents: {
    loadingState: LoadingState;
    items: AgencyAgentModel[];
  };
}

const initialState = {
  agencyInfo: {
    loadingState: LoadingState.NotLoaded,
    info: {
      agreements: [],
      agencyStates: [],
      branding: undefined,
      externalIntegrations: [],
    },
  },
  agents: {
    loadingState: LoadingState.NotLoaded,
    items: [],
  },
} satisfies AgenciesState as AgenciesState;

const slice = createSlice({
  name: 'agencies',
  initialState,
  reducers: {
    startLoadingAgencyInfo: (state) => {
      return {
        ...state,
        agencyInfo: {
          ...initialState.agencyInfo,
          loadingState: LoadingState.Loading,
        },
      };
    },
    agencyInfoLoaded: (state, action: PayloadAction<AgencyInfo>) => {
      const info = action.payload;
      return {
        ...state,
        agencyInfo: {
          info: { ...info },
          loadingState: LoadingState.Loaded,
        },
      };
    },
    startLoadingAgents: (state) => {
      return {
        ...state,
        agents: {
          ...initialState.agents,
          loadingState: LoadingState.Loading,
        },
      };
    },
    agentsLoaded: (state, action: PayloadAction<AgencyAgentModel[]>) => {
      return {
        ...state,
        agents: {
          items: action.payload,
          loadingState: LoadingState.Loaded,
        },
      };
    },
    errorLoadingAgents: (state) => {
      return {
        ...state,
        agents: {
          ...initialState.agents,
          loadingState: LoadingState.Failed,
        },
      };
    },
  },
});

export default slice.reducer;

const {
  startLoadingAgencyInfo,
  agencyInfoLoaded,
  startLoadingAgents,
  agentsLoaded,
  errorLoadingAgents,
} = slice.actions;

export const fetchAgents = (authToken: string) => async (dispatch: AppDispatch) => {
  try {
    dispatch(startLoadingAgents());
    const { data: agents } =
      await agenciesApi(authToken).get<AgencyAgentModel[]>('GetAgentsInAgency');

    dispatch(agentsLoaded(agents));

    return 'success';
  } catch (err: any) {
    console.error(err);
    dispatch(setAndShowErrorToast(err.message));
    dispatch(errorLoadingAgents());
    return err.message;
  }
};

export const useFetchAgencyInfo = () => {
  const dispatch = useAppDispatch();

  const { data: agencyInformation, isPending } = useGetAgencyInformationForUser();

  useEffect(() => {
    if (isPending) {
      dispatch(startLoadingAgencyInfo());
    } else if (!isPending && agencyInformation) {
      dispatch(agencyInfoLoaded(agencyInformation));
    }
  }, [agencyInformation, isPending]);
};
